import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Produkcja miesiąc po miesiącu — słupki z nałożoną linią zużycia.
 *
 * DLACZEGO ZUŻYCIE JAKO LINIA, A NIE DRUGI SŁUPEK: pytanie brzmi „czy w tym miesiącu
 * produkcja pokrywa zużycie", a nie „ile wynosi jedno i drugie". Linia przecinająca
 * słupki odpowiada na nie jednym spojrzeniem: latem słupki wystają ponad linię
 * (nadwyżka do sieci), zimą chowają się pod nią (dokupujesz).
 *
 * Słupek jest DWUCZĘŚCIOWY: dolna, ciemniejsza część to energia zużyta na miejscu,
 * górna, jaśniejsza — nadwyżka oddana do sieci. To ta pierwsza jest warta ponad
 * trzy razy więcej (patrz lib/oplacalnosc), więc musi być widoczna osobno.
 *
 * SVG, nie biblioteka wykresów: dwanaście słupków to kilkadziesiąt linijek, a każda
 * biblioteka doważyłaby paczkę frontu i własny sposób obsługi wydruku.
 */

const W = 640
const H = 210
const MARGINES = { gora: 14, dol: 26, lewo: 34, prawo: 8 }

const WykresProdukcji = ({ miesiace, pokazZuzycie = true }) => {
  const { t } = useTranslation('projekt')
  const [aktywny, setAktywny] = useState(null)
  if (!miesiace?.length) return null

  const maks = Math.max(
    ...miesiace.map((m) => Math.max(m.produkcjaKwh, pokazZuzycie ? m.zuzycieKwh : 0)),
  )
  // Skala do „ładnej" liczby w górę — oś z podziałką 137 kWh nic nikomu nie mówi.
  const krok = 10 ** Math.floor(Math.log10(maks)) / 2
  const gora = Math.ceil(maks / krok) * krok

  const polePlusY = H - MARGINES.dol
  const wysokoscPola = polePlusY - MARGINES.gora
  const szerPola = W - MARGINES.lewo - MARGINES.prawo
  const szerKolumny = szerPola / 12
  const szerSlupka = szerKolumny * 0.62

  const y = (v) => polePlusY - (v / gora) * wysokoscPola
  const xKolumny = (i) => MARGINES.lewo + i * szerKolumny
  const xSrodka = (i) => xKolumny(i) + szerKolumny / 2

  const maZuzycie = pokazZuzycie && miesiace.some((m) => m.zuzycieKwh > 0)
  const liniaZuzycia = maZuzycie
    ? miesiace.map((m, i) => `${i === 0 ? 'M' : 'L'} ${xSrodka(i)} ${y(m.zuzycieKwh)}`).join(' ')
    : null

  const podzialka = [0, 0.25, 0.5, 0.75, 1].map((f) => f * gora)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img"
           aria-label={t('wykres.aria')}>
        <defs>
          <linearGradient id="grad-slupek" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
        </defs>

        {podzialka.map((v) => (
          <g key={v}>
            {/* Siatka i opisy ze zmiennych motywu — SVG jest poza Tailwindem,
                a na sztywno wpisana jasna szarość świeciłaby w ciemnym motywie. */}
            <line x1={MARGINES.lewo} y1={y(v)} x2={W - MARGINES.prawo} y2={y(v)}
                  stroke="rgb(var(--vv-bd-gray-200))" strokeWidth="1" />
            <text x={MARGINES.lewo - 6} y={y(v) + 3} textAnchor="end"
                  fontSize="8.5" fill="rgb(var(--vv-tx-gray-400))">{Math.round(v)}</text>
          </g>
        ))}

        {miesiace.map((m, i) => {
          const x = xKolumny(i) + (szerKolumny - szerSlupka) / 2
          const wlasne = m.autokonsumpcjaKwh
          const yGora = y(m.produkcjaKwh)
          const wys = polePlusY - yGora
          const wysWlasne = wlasne != null ? polePlusY - y(wlasne) : 0
          const podswietlony = aktywny === i

          return (
            <g key={m.nazwa}
               onMouseEnter={() => setAktywny(i)} onMouseLeave={() => setAktywny(null)}
               onFocus={() => setAktywny(i)} onBlur={() => setAktywny(null)}
               tabIndex={0} style={{ cursor: 'pointer', outline: 'none' }}>
              {/* Pole trafienia na całą kolumnę — celowanie w wąski słupek zimą
                  (grudzień to kilka pikseli wysokości) byłoby udręką. */}
              <rect x={xKolumny(i)} y={MARGINES.gora} width={szerKolumny}
                    height={wysokoscPola} fill={podswietlony ? 'rgb(var(--vv-bg-gray-50))' : 'transparent'} />

              <rect x={x} y={yGora} width={szerSlupka} height={Math.max(0, wys)}
                    rx="2.5" fill="url(#grad-slupek)" opacity={wlasne != null ? 0.42 : 0.9} />
              {wlasne != null && wysWlasne > 0 && (
                <rect x={x} y={polePlusY - wysWlasne} width={szerSlupka}
                      height={wysWlasne} rx="2.5" fill="url(#grad-slupek)" />
              )}

              <text x={xSrodka(i)} y={H - 9} textAnchor="middle" fontSize="9"
                    fill={podswietlony ? 'rgb(var(--vv-tx-blue-800))' : 'rgb(var(--vv-tx-gray-400))'}
                    fontWeight={podswietlony ? 700 : 400}>
                {/* Skróty miesięcy przychodzą z lib po polsku („Sty”); tłumaczymy po numerze. */}
                {t(`wykres.miesiace.m${i + 1}`, { defaultValue: m.nazwa })}
              </text>

              {podswietlony && (
                <text x={xSrodka(i)} y={yGora - 5} textAnchor="middle" fontSize="9.5"
                      fontWeight="700" fill="rgb(var(--vv-tx-blue-800))">{Math.round(m.produkcjaKwh)}</text>
              )}
            </g>
          )
        })}

        {liniaZuzycia && (
          <>
            <path d={liniaZuzycia} fill="none" stroke="#f59e0b" strokeWidth="2"
                  strokeLinejoin="round" strokeLinecap="round" />
            {miesiace.map((m, i) => (
              <circle key={m.nazwa} cx={xSrodka(i)} cy={y(m.zuzycieKwh)} r="2.2" fill="#f59e0b" />
            ))}
          </>
        )}
      </svg>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 px-1 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-blue-600" /> {t('wykres.zuzyte_u_siebie')}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-blue-600 opacity-40" /> {t('wykres.oddane_do_sieci')}
        </span>
        {maZuzycie && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-0.5 bg-amber-500" /> {t('wykres.zuzycie_domu')}
          </span>
        )}
        <span className="text-gray-300">{t('wykres.jednostka')}</span>
      </div>
    </div>
  )
}

export default WykresProdukcji
